import { CSSProperties, PointerEvent as RPointerEvent, useEffect, useMemo, useRef } from 'react';
import { engine } from '../core/audio/AudioEngine';
import { Note, Pattern, noteName } from '../core/project/types';
import { useAppStore } from '../state/store';

const COL_W = 30;
const ROW_H = 20;
const TOP = 72; // C5
const BOTTOM = 36; // C2

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

interface DragState {
  type: 'move' | 'resize';
  noteId: string;
  startX: number;
  startY: number;
  origStart: number;
  origNote: number;
  origLen: number;
}

/**
 * Piano roll for synth channels: click to create, drag to move/transpose,
 * drag the right edge to resize, right-click to delete.
 */
export function PianoRoll({ pattern, color }: { pattern: Pattern; color: string }) {
  const { addNote, updateNote, removeNote } = useAppStore.getState();
  const playheadRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);

  const rows = useMemo(() => {
    const out: number[] = [];
    for (let n = TOP; n >= BOTTOM; n--) out.push(n);
    return out;
  }, []);

  const notesByRow = useMemo(() => {
    const m = new Map<number, Note[]>();
    for (const n of pattern.notes) {
      const arr = m.get(n.note) ?? [];
      arr.push(n);
      m.set(n.note, arr);
    }
    return m;
  }, [pattern.notes]);

  useEffect(() => {
    let raf = 0;
    const draw = () => {
      const el = playheadRef.current;
      if (el) {
        const pos = engine.positionStep % pattern.length;
        el.style.transform = `translateX(${pos * COL_W}px)`;
        el.style.opacity = engine.isPlaying ? '1' : '0';
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [pattern.length]);

  const startDrag = (e: RPointerEvent, n: Note, type: 'move' | 'resize') => {
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      type,
      noteId: n.id,
      startX: e.clientX,
      startY: e.clientY,
      origStart: n.start,
      origNote: n.note,
      origLen: n.length,
    };
  };

  const onDragMove = (e: RPointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    if (d.type === 'move') {
      const dSteps = Math.round((e.clientX - d.startX) / COL_W);
      const dNotes = -Math.round((e.clientY - d.startY) / ROW_H);
      updateNote(pattern.id, d.noteId, {
        start: clamp(d.origStart + dSteps, 0, pattern.length - d.origLen),
        note: clamp(d.origNote + dNotes, 0, 127),
      });
    } else {
      const dSteps = Math.round((e.clientX - d.startX) / COL_W);
      updateNote(pattern.id, d.noteId, {
        length: clamp(d.origLen + dSteps, 1, pattern.length - d.origStart),
      });
    }
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  const createNote = (e: RPointerEvent, rowNote: number) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const step = Math.floor((e.clientX - rect.left) / COL_W);
    if (step >= 0 && step < pattern.length) addNote(pattern.id, step, rowNote);
  };

  return (
    <div className="pr-scroll">
      <div className="pr-grid" style={{ '--ch-color': color } as CSSProperties}>
        <div className="pr-ruler">
          <div className="pr-rowlabel" />
          {Array.from({ length: pattern.length }, (_, i) => (
            <div key={i} className={`pr-stepnum ${i % 4 === 0 ? 'beat' : ''}`}>
              {i % 4 === 0 ? i / 4 + 1 : ''}
            </div>
          ))}
        </div>
        <div className="pr-body">
          {rows.map((n) => (
            <div key={n} className={`pr-row ${n % 12 === 0 ? 'is-c' : ''}`}>
              <div className="pr-rowlabel">{noteName(n)}</div>
              <div className="pr-cells" onPointerDown={(e) => createNote(e, n)}>
                {(notesByRow.get(n) ?? []).map((note) => (
                  <div
                    key={note.id}
                    className="pr-note"
                    style={{ left: note.start * COL_W, width: note.length * COL_W - 1 }}
                    onPointerDown={(e) => startDrag(e, note, 'move')}
                    onPointerMove={onDragMove}
                    onPointerUp={endDrag}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeNote(pattern.id, note.id);
                    }}
                    title={`${noteName(note.note)} · ${note.length} step${note.length > 1 ? 's' : ''} · vel ${note.velocity} — right-click to delete`}
                  >
                    <div
                      className="pr-note-grip"
                      onPointerDown={(e) => startDrag(e, note, 'resize')}
                      onPointerMove={onDragMove}
                      onPointerUp={endDrag}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="pr-playhead" ref={playheadRef} />
        </div>
      </div>
    </div>
  );
}